import { inject } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { EventAggregator } from 'aurelia-event-aggregator';

import { CoatingService } from 'pages/system-fields/coatings/service';
import { CommodityService } from 'pages/system-fields/commodities/service';
import { MaterialService } from 'pages/system-fields/materials/service';
import { StatusService } from 'pages/system-fields/statuses/service';
import { PurchasePartTypeService } from 'pages/system-fields/purchase-part-types/service';
import { SpecificGravityService } from 'pages/system-fields/specific-gravities/service';
import { StageService } from 'pages/system-fields/stages/service';
import { UnitOfMeasurementService } from 'pages/system-fields/unit-of-measurements/service';
import { PlantService } from 'pages/quote-setup/plants/service';
import { CustomerService } from 'pages/quote-setup/customers/service';
import { ProcessService } from 'pages/standard-setup/processes/service';
import { AssignedProjectService } from 'pages/daiatari/assigned-projects/service';
import { BillOfMaterialService } from 'pages/quote-setup/bill-of-material/service';
import { CustomerProjectService } from 'pages/standard-setup/customer-projects/service';
import { MachineService } from 'pages/standard-setup/machines/service';
import { PartService } from 'pages/quote-setup/parts/service';
import { PartSizeService } from 'pages/standard-setup/part-sizes/service';
import { ProjectService } from 'pages/quote-setup/projects/service';
import { PurchaseItemPriceService } from 'pages/quote-setup/purchase-item-prices/service';
import { PurchaseTypeRuleService } from 'pages/quote-setup/purchase-type-rules/service';
import { QuoteService } from 'pages/quotes/service';
import { StandardService } from 'pages/standard-setup/standards/service';

import 'resources/elements/search/styles.scss';

@inject(
    DialogService, EventAggregator, PlantService, StatusService, AssignedProjectService, 
    CustomerService, CoatingService, CommodityService, MaterialService, 
    PurchasePartTypeService, SpecificGravityService, ProcessService,
    StageService, UnitOfMeasurementService, BillOfMaterialService, CustomerProjectService,
    MachineService, PartService, PartSizeService, ProjectService,
    PurchaseItemPriceService, PurchaseTypeRuleService, QuoteService, StandardService)
export class Search {
    constructor (
        dialogService, ea, plantService, statusService, assignedProjectService, 
        customerService, coatingService, commodityService, materialService, 
        purchasePartTypeService, specificGravityService, processService,
        stageService, unitOfMeasurementService, billOfMaterialService, customerProjectService,
        machineService, partService, partSizeService, projectService,
        purchaseItemPriceService, purchaseTypeRuleService, quoteService, standardService) {
        this.ea = ea;
        
        this.dialogService = dialogService;
        this.services = { 
            'plantService': plantService,
            'statusService': statusService,
            'assignedProjectService': assignedProjectService,
            'customerService': customerService,
            'coatingService': coatingService,
            'commodityService': commodityService,
            'materialService': materialService,
            'purchasePartTypeService': purchasePartTypeService,
            'specificGravityService': specificGravityService,
            'processService': processService,
            'stageService': stageService,
            'unitOfMeasurementService': unitOfMeasurementService,
            'billOfMaterialService': billOfMaterialService,
            'customerProjectService': customerProjectService,
            'machineService': machineService,
            'partService': partService,
            'partSizeService': partSizeService,
            'processService': processService,
            'projectService': projectService,
            'purchaseItemPriceService': purchaseItemPriceService,
            'purchaseTypeRuleService': purchaseTypeRuleService,
            'quoteService': quoteService,
            'standardService': standardService
        };

        this.selectedCategory = null;
        this.items = [];
        this.focusIndex = -1;

        this.categories = [
            { id: 'assignedProjectService', name: 'Assigned Projects' },
            { id: 'billOfMaterialService', name: 'Bill of Material' },
            { id: 'coatingService', name: 'Coatings' },
            { id: 'commodityService', name: 'Commodities' },
            { id: 'customerService', name: 'Customers' },
            { id: 'customerProjectService', name: 'Customer Projects' },
            { id: 'machineService', name: 'Machines' },
            { id: 'materialService', name: 'Materials' },
            { id: 'partService', name: 'Parts' },
            { id: 'partSizeService', name: 'Part Sizes' },
            { id: 'plantService', name: 'Plants' },
            { id: 'processService', name: 'Processes' },
            { id: 'projectService', name: 'Projects' },
            { id: 'purchaseItemPriceService', name: 'Purchase Item Prices' },
            { id: 'purchasePartTypeService', name: 'Purchase Part Types' },
            { id: 'purchaseTypeRuleService', name: 'Purchase Type Rules' },
            { id: 'quoteService', name: 'Quotes' },
            { id: 'specificGravityService', name: 'Specific Gravities' },
            { id: 'standardService', name: 'Standards' },
            { id: 'stageService', name: 'Stages' },
            { id: 'statusService', name: 'Statuses' },
            { id: 'unitOfMeasurementService', name: 'Unit of Measurements' },
        ];
    }

    blur(event) {
        setTimeout(() => this.clearItems(), 100);
    }

    clearItems() {
        this.items.splice(0, this.items.length);
    }

    clearCategory() {
        this.selectedCategory = null;
    }

    keyDown(event) {
        if (event.key === 'Backspace') {
            if (event.target.value.length === 0 && this.selectedCategory != null) {
                this.clearCategory();
            }
        }

        return true;
    }

    async search(event) {
        if (event.key === 'ArrowDown') {
            if (this.focusIndex < this.items.length -1) {
                this.focusIndex++;
            }
            return;
        }

        if (event.key === 'ArrowUp') {
            if (this.focusIndex > 0) {
                this.focusIndex--;
            }
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            if (this.focusIndex > -1) {
                this.selectItem(this.items[this.focusIndex]);
            }
            return;
        }

        this.focusIndex = -1;

        this.searchText = event.target.value;

        this.items.splice(0, this.items.length);

        if (this.selectedCategory == null) {
            this.filterCategory();
        } else {
            await this.filterQuery();
        }
    }

    filterCategory() {
        for(let category of this.categories) {
            if (this.searchText.length > 0 && !category.name.toLocaleLowerCase().includes(this.searchText.toLocaleLowerCase())) {
                continue;
            }
            this.items.push(category);
        }
    }

    async filterQuery() {
        const filterService = this.services[this.selectedCategory.id];
        const items = await filterService.filterTopThree(this.searchText);
        for(let item of items) {
            this.items.push(item);
        }
    }

    selectItem(item) {
        this.textInput.value = '';

        if (this.selectedCategory == null) {
            this.selectedCategory = item;
            this.clearItems();
            return;
        }

        const filterService = this.services[this.selectedCategory.id];

        this.dialogService.open({ viewModel: filterService.editor, model: { id: item.id } });
    }
}